import React, { useEffect } from 'react';
import { Alert, Box, IconButton, Button } from '@mui/material';
import {
    CheckCircleRounded, CloseOutlined, ErrorOutline,
    InfoOutlined, WarningAmberOutlined,
} from '@mui/icons-material';
import { createPortal } from 'react-dom';
import { makeStyles } from '@mui/styles';

type Severity = 'success' | 'error' | 'info' | 'warning';
type Anchor = { vertical: 'top' | 'bottom'; horizontal: 'left' | 'center' | 'right' };

const useStyles = makeStyles({
    container: {
        position: 'fixed',
        zIndex: 1400,
        pointerEvents: 'none',
    },
    topLeft: { top: 8, left: 24 },
    topCenter: { top: 8, left: '50%', transform: 'translateX(-50%)' },
    topRight: { top: 8, right: 24 },
    bottomLeft: { bottom: 24, left: 24 },
    bottomCenter: { bottom: 24, left: '50%', transform: 'translateX(-50%)' },
    bottomRight: { bottom: 24, right: 24 },
    toastAlert: {
        '& .MuiAlert-icon': {
            color: '#fff !important',
            opacity: 1,
        },
        '& .MuiAlert-icon .MuiSvgIcon-root': {
            color: '#fff !important',
        },
        '& .MuiAlert-action .MuiIconButton-root': {
            color: '#fff !important',
            '&:hover': { color: '#fff !important' },
        },
        '& .MuiAlert-action .MuiSvgIcon-root': {
            color: '#fff !important',
        },
        '& .MuiAlert-message': {
            color: '#fff !important',
            whiteSpace: 'nowrap',
        },
    },
});

interface Props {
    open: boolean;
    message: unknown;
    severity: Severity;
    autoHideMs?: number | false;
    onClose: () => void;
    onSave?: () => void;
    saveLabel?: string;
    saveDisabled?: boolean;
    anchor?: Anchor;
}

const CustomToast: React.FC<Props> = ({
    open,
    message,
    severity,
    autoHideMs = 2500,
    onClose,
    onSave,
    saveLabel = 'Save',
    saveDisabled = false,
    anchor = { vertical: 'bottom', horizontal: 'center' },
}) => {
    const classes = useStyles();

    const renderMessage = (m: unknown): React.ReactNode => {
        // already renderable?
        if (typeof m === 'string' || typeof m === 'number') return m;
        if (React.isValidElement(m)) return m;

        const data: any = m;

        // FastAPI / Pydantic common shape: { detail: [ {loc, msg, type} | string, ... ] }
        if (data?.detail && Array.isArray(data.detail)) {
            return data.detail
                .map((e: any) =>
                    typeof e === 'string'
                        ? e
                        : `${Array.isArray(e.loc) ? e.loc.join('.') : ''}${e.loc ? ': ' : ''}${e.msg ?? ''}`.trim()
                )
                .filter(Boolean)
                .join(' • ');
        }

        // { message: '...' }
        if (typeof data?.message === 'string') return data.message;

        // plain array -> join safely
        if (Array.isArray(data)) {
            return data
                .map((x) => (typeof x === 'string' ? x : (() => { try { return JSON.stringify(x); } catch { return String(x); } })()))
                .join(' • ');
        }

        // last resort
        try { return JSON.stringify(data); } catch { return String(data); }
    };

    useEffect(() => {
        if (!open || autoHideMs === false) return;
        const t = setTimeout(onClose, autoHideMs);
        return () => clearTimeout(t);
    }, [open, autoHideMs, onClose]);

    if (!open) return null;

    const anchorKey =
        (anchor.vertical === 'top' ? 'top' : 'bottom') +
        (anchor.horizontal.charAt(0).toUpperCase() + anchor.horizontal.slice(1)) as
        | 'topLeft' | 'topCenter' | 'topRight'
        | 'bottomLeft' | 'bottomCenter' | 'bottomRight';

    return createPortal(
        <Box className={`${classes.container} ${classes[anchorKey]}`}>
            <Box sx={{ pointerEvents: 'auto' }}>
                <Alert
                    variant="filled"
                    severity={severity}
                    onClose={onClose}
                    className={classes.toastAlert}
                    iconMapping={{
                        success: <CheckCircleRounded fontSize="small" sx={{ color: '#fff' }} />,
                        error: <ErrorOutline fontSize="small" sx={{ color: '#fff' }} />,
                        info: <InfoOutlined fontSize="small" sx={{ color: '#fff' }} />,
                        warning: <WarningAmberOutlined fontSize="small" sx={{ color: '#fff' }} />,
                    }}
                    action={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                            {onSave && (
                                <Button
                                    size="small"
                                    variant="contained"
                                    disableElevation
                                    onClick={onSave}
                                    disabled={saveDisabled}
                                    sx={{
                                        bgcolor: 'rgba(255,255,255,0.25)',
                                        color: '#fff',
                                        '&:hover': { bgcolor: 'rgba(255,255,255,0.35)' },
                                        fontWeight: 700,
                                    }}
                                >
                                    {String(saveLabel).toUpperCase()}
                                </Button>
                            )}
                            <IconButton size="small" onClick={onClose} sx={{ color: '#fff' }}>
                                <CloseOutlined fontSize="small" />
                            </IconButton>
                        </Box>
                    }
                >
                    {renderMessage(message)}
                </Alert>
            </Box>
        </Box>,
        document.body
    );
};

export default CustomToast;
